<template>
    <!-- checkbox: a descrição inteira é o alvo do clique -->
    <div v-if="field.type === 'checkbox'" class="cred-field cred-field-check">
        <label class="cred-check" :for="inputId">
            <input type="checkbox"
                :id="inputId"
                ref="input"
                v-model="field.value"
                :disabled="disabled || !!field.readOnly"
                :aria-describedby="field.info ? helpId : null" />
            <span class="cred-check-box" aria-hidden="true">
                <fa icon="check" />
            </span>
            <span class="cred-check-label">{{field.description}}</span>
        </label>
        <p class="cred-help" :id="helpId" v-if="field.info">{{field.info}}</p>
    </div>

    <div v-else class="cred-field" :class="{ 'is-invalid': !!requiredError, 'has-warning': !!visibleWarning, 'is-highlighted': highlighted }">
        <label class="cred-label" :for="inputId">
            {{field.description}}
            <span class="cred-optional" v-if="!field.required && !field.readOnly">(opcional)</span>
        </label>

        <div class="cred-control" :class="{ 'is-readonly': !!field.readOnly }">
            <span class="cred-control-icon" aria-hidden="true">
                <fa :icon="leadingIcon" />
            </span>

            <textarea v-if="isMultiline"
                :id="inputId"
                ref="input"
                class="cred-input is-mono"
                v-model="field.value"
                :rows="rows"
                :placeholder="placeholder"
                :readonly="!!field.readOnly"
                :disabled="disabled"
                :aria-invalid="!!requiredError"
                :aria-describedby="describedBy"
                autocomplete="off"
                autocapitalize="off"
                spellcheck="false"
                @blur="onBlur"></textarea>

            <input v-else
                :id="inputId"
                ref="input"
                class="cred-input"
                :class="{ 'is-mono': isMono }"
                :type="isSecret && !revealed ? 'password' : 'text'"
                v-model="field.value"
                :placeholder="placeholder"
                :readonly="!!field.readOnly"
                :disabled="disabled"
                :aria-invalid="!!requiredError"
                :aria-describedby="describedBy"
                autocomplete="off"
                autocapitalize="off"
                spellcheck="false"
                @blur="onBlur"
                @paste="onPaste" />

            <span class="cred-control-actions">
                <button type="button"
                    class="cred-icon-button"
                    v-if="isSecret && !isMultiline"
                    :aria-pressed="revealed ? 'true' : 'false'"
                    :aria-label="revealed ? `Ocultar ${field.description}` : `Mostrar ${field.description}`"
                    :title="revealed ? 'Ocultar' : 'Mostrar'"
                    @click="revealed = !revealed">
                    <fa :icon="revealed ? 'eye-slash' : 'eye'" />
                </button>

                <button type="button"
                    class="cred-icon-button"
                    v-if="copyable && hasValue"
                    :aria-label="`Copiar ${field.description}`"
                    :title="copied ? 'Copiado' : 'Copiar'"
                    @click="copy()">
                    <fa :icon="copied ? 'check' : ['far', 'copy']" />
                </button>
            </span>
        </div>

        <p class="cred-feedback is-error" :id="feedbackId" v-if="requiredError" role="alert">
            <fa icon="exclamation-triangle" />{{requiredError}}
        </p>
        <p class="cred-feedback is-warning" :id="feedbackId" v-else-if="visibleWarning">
            <fa icon="exclamation-triangle" />{{visibleWarning}}
        </p>

        <p class="cred-help" :id="helpId" v-if="field.info">{{field.info}}</p>
        <span class="sr-only" aria-live="polite">{{copied ? 'Copiado' : ''}}</span>
    </div>
</template>

<script>
import credentialsFieldHints from '@/channel/utils/CredentialsFieldHints'
import credentialsFormHelper from '@/channel/utils/CredentialsFormHelper'

const COPIED_FEEDBACK_MS = 1800

/**
 * Um campo do schema de credenciais. Edita `field.value` do próprio form
 * (mesmo objeto que o v-model antigo editava), então quem salva continua igual.
 */
export default {
    name: 'CredentialFieldCmp',
    props: {
        fieldKey: {
            type: String,
            required: true,
        },
        field: {
            type: Object,
            required: true,
        },
        channelEnum: {
            type: [Number, String],
            default: null,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        // Liga as mensagens de obrigatório (só depois de tentar salvar)
        submitted: {
            type: Boolean,
            default: false,
        },
        copyable: {
            type: Boolean,
            default: false,
        },
        highlighted: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            touched: false,
            revealed: false,
            copied: false,
        }
    },
    computed: {
        inputId() {
            return `cred-${this.fieldKey}-${this._uid}`
        },
        helpId() {
            return `${this.inputId}-help`
        },
        feedbackId() {
            return `${this.inputId}-feedback`
        },
        describedBy() {
            const ids = []

            if (this.requiredError || this.visibleWarning) {
                ids.push(this.feedbackId)
            }

            if (this.field.info) {
                ids.push(this.helpId)
            }

            return ids.join(' ') || null
        },
        isSecret() {
            return credentialsFormHelper.isSecretField(this.fieldKey)
        },
        isMono() {
            return credentialsFormHelper.isMonospaceField(this.fieldKey)
        },
        // Certificado, JSON e afins: só vira área de texto se o valor já tiver quebra de linha
        isMultiline() {
            return typeof this.field.value === 'string' && this.field.value.includes('\n')
        },
        rows() {
            return Math.min(8, Math.max(3, (this.field.value || '').split('\n').length))
        },
        leadingIcon() {
            if (this.field.readOnly) {
                return 'lock'
            }

            if (/url|host|endpoint|domain|dominio/i.test(this.fieldKey)) {
                return 'link'
            }

            return this.isSecret || /key|token/i.test(this.fieldKey) ? 'key' : 'user'
        },
        placeholder() {
            return credentialsFieldHints.getPlaceholder(this.channelEnum, this.fieldKey)
        },
        hasValue() {
            return typeof this.field.value === 'string' ? !!this.field.value.trim() : !!this.field.value
        },
        requiredError() {
            if (!this.submitted || !this.field.required || this.field.readOnly || this.hasValue) {
                return ''
            }

            return `Informe ${this.field.description || 'este campo'}.`
        },
        // Aviso de formato espera o usuário sair do campo: não grita no meio da digitação
        visibleWarning() {
            if (!this.touched && !this.submitted) {
                return ''
            }

            return credentialsFieldHints.getFormatWarning(this.channelEnum, this.fieldKey, this.field.value)
        },
    },
    methods: {
        focus() {
            this.$refs.input && this.$refs.input.focus()
        },
        onBlur() {
            this.touched = true

            if (typeof this.field.value === 'string' && this.field.value !== this.field.value.trim()) {
                this.field.value = this.field.value.trim()
            }
        },
        // Valor colado já pode ser conferido: é o momento em que mais se cola a chave errada
        onPaste() {
            this.$nextTick(() => {
                this.touched = true
            })
        },
        async copy() {
            const value = String(this.field.value || '')

            try {
                await navigator.clipboard.writeText(value)
            } catch (e) {
                const helper = document.createElement('textarea')
                helper.value = value
                helper.setAttribute('readonly', '')
                helper.style.position = 'fixed'
                helper.style.opacity = '0'
                document.body.appendChild(helper)
                helper.select()
                document.execCommand('copy')
                document.body.removeChild(helper)
            }

            this.copied = true
            clearTimeout(this.copiedTimer)
            this.copiedTimer = setTimeout(() => {
                this.copied = false
            }, COPIED_FEEDBACK_MS)
        },
    },
    beforeDestroy() {
        clearTimeout(this.copiedTimer)
    },
}
</script>

<style lang="scss" scoped>
$ease-out: cubic-bezier(.22, 1, .36, 1);
$mono: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;

.cred-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.cred-label {
    margin: 0;
    color: var(--text-strong);
    font-size: 14px;
    font-weight: 700;
}

.cred-optional {
    margin-left: 4px;
    color: var(--text-faint);
    font-weight: 500;
}

/* ── Controle ── */

.cred-control {
    position: relative;
    display: flex;
    align-items: stretch;
    border: 1px solid var(--border);
    border-radius: 10px;
    background: var(--surface-1);
    transition: border-color .15s, box-shadow .15s;

    &:hover {
        border-color: var(--border-strong);
    }

    &:focus-within {
        border-color: var(--brand-soft);
        box-shadow: 0 0 0 3px color-mix(in srgb, var(--brand-soft) 22%, transparent);
    }

    &.is-readonly {
        background: var(--surface-disabled);
    }

    .is-invalid & {
        border-color: var(--danger-solid);

        &:focus-within {
            box-shadow: 0 0 0 3px color-mix(in srgb, var(--danger-solid) 20%, transparent);
        }
    }

    .has-warning & {
        border-color: var(--warning-solid);
    }

    .is-highlighted & {
        animation: cred-highlight 1.6s $ease-out both;
    }
}

.cred-control-icon {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 40px;
    color: var(--text-faint);
    font-size: 13px;

    .cred-control:focus-within & {
        color: var(--brand-soft);
    }
}

.cred-input {
    flex: 1 1 auto;
    min-width: 0;
    min-height: 44px;
    padding: 10px 12px 10px 0;
    border: 0;
    background: transparent;
    color: var(--text-strong);
    font-size: 15px;
    outline: none;

    &::placeholder {
        color: var(--text-disabled);
    }

    &.is-mono {
        font-family: $mono;
        font-size: 14px;
        letter-spacing: -.1px;
    }

    &[readonly] {
        color: var(--text-soft);
    }

    &:disabled {
        cursor: not-allowed;
        opacity: .7;
    }
}

textarea.cred-input {
    resize: vertical;
}

.cred-control-actions {
    display: flex;
    align-items: center;
    padding-right: 4px;

    &:empty {
        display: none;
    }
}

.cred-icon-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: var(--text-muted);
    cursor: pointer;
    transition: background-color .15s, color .15s;

    &:hover {
        background: var(--surface-3);
        color: var(--text-strong);
    }

    &:focus-visible {
        outline: 2px solid var(--brand-soft);
        outline-offset: 1px;
    }
}

/* ── Mensagens ── */

.cred-feedback {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    margin: 0;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.4;
    animation: cred-in .25s $ease-out both;

    svg {
        flex-shrink: 0;
        margin-top: 2px;
    }

    &.is-error {
        color: var(--danger-fg);
    }

    &.is-warning {
        color: var(--warning-fg);
    }
}

.cred-help {
    margin: 0;
    color: var(--text-muted);
    font-size: 13px;
    line-height: 1.45;
}

/* ── Checkbox ── */

.cred-check {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin: 0;
    cursor: pointer;

    input {
        position: absolute;
        opacity: 0;
        pointer-events: none;
    }
}

.cred-check-box {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    margin-top: 1px;
    border: 1.5px solid var(--border-strong);
    border-radius: 6px;
    background: var(--surface-1);
    color: transparent;
    font-size: 11px;
    transition: background-color .15s, border-color .15s, color .15s;

    input:checked + & {
        border-color: var(--brand-fill);
        background: var(--brand-fill);
        color: var(--text-on-brand);
    }

    input:focus-visible + & {
        box-shadow: 0 0 0 3px color-mix(in srgb, var(--brand-soft) 30%, transparent);
    }

    input:disabled + & {
        opacity: .6;
    }
}

.cred-check-label {
    color: var(--text-strong);
    font-weight: 600;
}

.cred-field-check .cred-help {
    padding-left: 30px;
}

@keyframes cred-in {
    from { opacity: 0; transform: translateY(-3px); }
    to   { opacity: 1; transform: translateY(0); }
}

@keyframes cred-highlight {
    0%, 40% {
        border-color: var(--success-solid);
        box-shadow: 0 0 0 4px color-mix(in srgb, var(--success-solid) 25%, transparent);
    }
}

@media (prefers-reduced-motion: reduce) {
    .cred-feedback,
    .cred-control {
        animation: none !important;
    }
}
</style>
